import React from 'react'
import { cn } from '../../utils'

/**
 * Card component with adaptive design
 * Provides consistent container styling with responsive behavior
 */

const Card = React.forwardRef(({
  className,
  adaptive = true,
  hover = false,
  padding = 'default',
  children,
  ...props
}, ref) => {
  const paddingVariants = {
    none: '',
    sm: 'p-3',
    default: 'p-4 lg:p-6',
    lg: 'p-6 lg:p-8',
    xl: 'p-8 lg:p-10'
  }
  
  return (
    <div
      ref={ref}
      className={cn(
        // Base card styles
        'bg-white rounded-lg border border-border-primary',
        'shadow-sm',
        
        // Adaptive enhancements
        adaptive && [
          'lg:shadow-md lg:hover:shadow-lg',
          'transition-all duration-200 ease-in-out'
        ],
        
        // Hover effects (desktop only)
        hover && [
          'lg:hover:scale-[1.02] lg:hover:shadow-lg',
          'cursor-pointer'
        ],
        
        // Padding
        paddingVariants[padding],
        
        // Adaptive padding
        adaptive && 'p-adaptive-md lg:p-adaptive-lg',
        
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})

Card.displayName = 'Card'

const CardHeader = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex flex-col space-y-1.5 pb-4',
      'lg:pb-6',
      className
    )}
    {...props}
  >
    {children}
  </div>
))

CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef(({
  className,
  adaptive = true,
  children,
  ...props
}, ref) => (
  <h3
    ref={ref}
    className={cn(
      'font-semibold leading-none tracking-tight',
      adaptive ? 'text-adaptive-lg' : 'text-lg',
      className
    )}
    {...props}
  >
    {children}
  </h3>
))

CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef(({
  className,
  adaptive = true,
  children,
  ...props
}, ref) => (
  <p
    ref={ref}
    className={cn(
      'text-text-secondary',
      adaptive ? 'text-adaptive-sm' : 'text-sm',
      className
    )}
    {...props}
  >
    {children}
  </p>
))

CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => (
  <div
    ref={ref}
    className={cn('pt-0', className)}
    {...props}
  >
    {children}
  </div>
))

CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef(({
  className,
  children,
  ...props
}, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-center pt-4',
      'lg:pt-6',
      className
    )}
    {...props}
  >
    {children}
  </div>
))

CardFooter.displayName = 'CardFooter'

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
}